import { useState, useEffect } from 'react';

const API_URL = 'https://api-monitoring.csdev.space';

function App() {
  const [view, setView] = useState('sites'); // sites, clients, payments, client-detail
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('site');
  const [editingItem, setEditingItem] = useState(null);
  const [formError, setFormError] = useState('');
  
  // Form states
  const [siteForm, setSiteForm] = useState({ name: '', url: '', client_ids: [] });
  const [clientForm, setClientForm] = useState({ 
    name: '', siret: '', email: '', phone: '', address: '', city: '', postal_code: '', notes: '' 
  });
  const [contractForm, setContractForm] = useState({ 
    type: 'annuel', start_date: '', end_date: '', monthly_amount: '', notes: '' 
  });
  const [paymentForm, setPaymentForm] = useState({
    client_id: '', amount: '', due_date: '', status: 'pending', payment_method: '', notes: ''
  });
  const [historyData, setHistoryData] = useState(null);

  useEffect(() => {
    if (token) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchData = async () => {
    await Promise.all([fetchSites(), fetchClients(), fetchStats(), fetchPayments()]);
    setLoading(false);
  };

  const fetchSites = async () => {
    try {
      let url = `${API_URL}/api/sites`;
      if (clientFilter) url += `?client_id=${clientFilter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) { logout(); return; }
      const data = await response.json();
      setSites(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Erreur clients:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Erreur payments:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedClient(data);
        setView('client-detail');
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
      } else {
        setError(data.error);
      }
    } catch {
      setError('Erreur de connexion');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setSites([]);
    setClients([]);
    setStats(null);
  };

  // ============ MODALS ============

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setFormError('');
    
    if (type === 'site') {
      setSiteForm(item ? { 
        name: item.name, 
        url: item.url, 
        client_ids: item.clients?.map(c => c.id) || [] 
      } : { name: '', url: '', client_ids: [] });
    } else if (type === 'client') {
      setClientForm(item ? {
        name: item.name || '',
        siret: item.siret || '',
        email: item.email || '',
        phone: item.phone || '',
        address: item.address || '',
        city: item.city || '',
        postal_code: item.postal_code || '',
        notes: item.notes || ''
      } : { name: '', siret: '', email: '', phone: '', address: '', city: '', postal_code: '', notes: '' });
    } else if (type === 'contract') {
      setContractForm(item?.contract?.id ? {
        type: item.contract.type || 'annuel',
        start_date: item.contract.start_date?.split('T')[0] || '',
        end_date: item.contract.end_date?.split('T')[0] || '',
        monthly_amount: item.contract.monthly_amount || '',
        notes: item.contract.notes || ''
      } : { type: 'annuel', start_date: '', end_date: '', monthly_amount: '', notes: '' });
    } else if (type === 'payment') {
      setPaymentForm(item ? {
        client_id: item.client_id || '',
        amount: item.amount || '',
        due_date: item.due_date?.split('T')[0] || '',
        status: item.status || 'pending',
        paid_date: item.paid_date?.split('T')[0] || '',
        payment_method: item.payment_method || '',
        notes: item.notes || ''
      } : { client_id: '', amount: '', due_date: '', status: 'pending', payment_method: '', notes: '' });
    }
    
    setShowModal(true);
  };

  const openHistoryModal = async (site) => {
    setModalType('history');
    setEditingItem(site);
    setHistoryData(null);
    setShowModal(true);
    
    try {
      const response = await fetch(`${API_URL}/api/sites/${site.id}/history?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryData(data);
      }
    } catch (error) {
      console.error('Erreur historique:', error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setHistoryData(null);
    setFormError('');
  };

  // ============ CRUD HANDLERS ============

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    if (!siteForm.name || !siteForm.url) {
      setFormError('Nom et URL requis');
      return;
    }
    try {
      const url = editingItem ? `${API_URL}/api/sites/${editingItem.id}` : `${API_URL}/api/sites`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(siteForm)
      });
      if (response.ok) {
        closeModal();
        fetchData();
      } else {
        const data = await response.json();
        setFormError(data.error || 'Erreur');
      }
    } catch {
      setFormError('Erreur de connexion');
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!clientForm.name) {
      setFormError('Nom requis');
      return;
    }
    try {
      const url = editingItem ? `${API_URL}/api/clients/${editingItem.id}` : `${API_URL}/api/clients`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(clientForm)
      });
      if (response.ok) {
        closeModal();
        fetchData();
        if (selectedClient && editingItem?.id === selectedClient.id) {
          fetchClientDetail(selectedClient.id);
        }
      } else {
        const data = await response.json();
        setFormError(data.error || 'Erreur');
      }
    } catch {
      setFormError('Erreur de connexion');
    }
  };

  const handleContractSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/sites/${editingItem.id}/contract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(contractForm)
      });
      if (response.ok) {
        closeModal();
        fetchData();
      } else {
        const data = await response.json();
        setFormError(data.error || 'Erreur');
      }
    } catch {
      setFormError('Erreur de connexion');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.client_id || !paymentForm.amount || !paymentForm.due_date) {
      setFormError('Client, montant et date requis');
      return;
    }
    try {
      const url = editingItem ? `${API_URL}/api/payments/${editingItem.id}` : `${API_URL}/api/payments`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(paymentForm)
      });
      if (response.ok) {
        closeModal();
        fetchData();
        if (selectedClient) fetchClientDetail(selectedClient.id);
      } else {
        const data = await response.json();
        setFormError(data.error || 'Erreur');
      }
    } catch {
      setFormError('Erreur de connexion');
    }
  };

  const handleDelete = async (type, id) => {
    const confirmMsg = type === 'site' ? 'ce site' : type === 'client' ? 'ce client' : 'ce paiement';
    if (!window.confirm(`Supprimer ${confirmMsg} ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/${type}s/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
        if (type === 'client' && selectedClient?.id === id) {
          setView('clients');
          setSelectedClient(null);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const checkSSL = async (site) => {
    try {
      await fetch(`${API_URL}/api/sites/${site.id}/check-ssl`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Erreur SSL:', error);
    }
  };

  const markPaymentPaid = async (payment) => {
    try {
      await fetch(`${API_URL}/api/payments/${payment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...payment, status: 'paid', paid_date: new Date().toISOString().split('T')[0] })
      });
      fetchData();
      if (selectedClient) fetchClientDetail(selectedClient.id);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // ============ HELPERS ============

  const getStatusColor = (site) => {
    if (!site.last_check?.checked_at) return 'bg-gray-500';
    return site.last_check.is_up ? 'bg-green-500' : 'bg-red-500';
  };

  const getStatusText = (site) => {
    if (!site.last_check?.checked_at) return 'Non vérifié';
    return site.last_check.is_up ? `OK (${site.last_check.response_time_ms}ms)` : `DOWN`;
  };

  const getContractBadge = (contract) => {
    if (!contract?.type) return null;
    const colors = { locam: 'bg-purple-600', perso: 'bg-blue-600', annuel: 'bg-green-600' };
    return <span className={`${colors[contract.type] || 'bg-gray-600'} text-xs px-2 py-1 rounded`}>{contract.type.toUpperCase()}</span>;
  };

  const getDaysLeft = (date) => {
    if (!date) return null;
    return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const getPaymentStatusBadge = (status) => {
    const colors = { pending: 'bg-yellow-600', paid: 'bg-green-600', late: 'bg-red-600', cancelled: 'bg-gray-600' };
    const labels = { pending: 'En attente', paid: 'Payé', late: 'En retard', cancelled: 'Annulé' };
    return <span className={`${colors[status]} text-xs px-2 py-1 rounded`}>{labels[status]}</span>;
  };

  const filteredSites = sites.filter(site => {
    const matchesFilter = filter === 'all' || (filter === 'up' && site.last_check?.is_up) || (filter === 'down' && !site.last_check?.is_up);
    const matchesSearch = !searchTerm || site.name.toLowerCase().includes(searchTerm.toLowerCase()) || site.clients?.some(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // ============ LOGIN ============

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <form onSubmit={login} className="bg-gray-800 p-8 rounded-lg w-80">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">🔐 COSAR Monitor</h1>
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full p-3 rounded bg-gray-700 text-white mb-4 outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded font-semibold">
            Connexion
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  // ============ MAIN LAYOUT ============

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-gray-800 p-4">
        <h1 className="text-xl font-bold text-white mb-8">🖥️ COSAR Monitor</h1>
        
        <nav className="space-y-2">
          <button
            onClick={() => setView('sites')}
            className={`w-full text-left px-4 py-2 rounded ${view === 'sites' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            📊 Sites ({stats?.total_sites || 0})
          </button>
          <button
            onClick={() => setView('clients')}
            className={`w-full text-left px-4 py-2 rounded ${view === 'clients' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            👥 Clients ({stats?.total_clients || 0})
          </button>
          <button
            onClick={() => setView('payments')}
            className={`w-full text-left px-4 py-2 rounded ${view === 'payments' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            💰 Paiements
          </button>
        </nav>

        {/* Stats rapides */}
        {stats && (
          <div className="mt-8 space-y-2 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>En ligne</span>
              <span className="text-green-400">{stats.sites_up}</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Hors ligne</span>
              <span className="text-red-400">{stats.sites_down}</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Contrats expirants</span>
              <span className="text-yellow-400">{stats.expiring_contracts}</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Paiements en retard</span>
              <span className="text-red-400">{stats.late_payments}</span>
            </div>
            <div className="flex justify-between text-gray-400 pt-4 border-t border-gray-700">
              <span>CA du mois</span>
              <span className="text-green-400">{stats.monthly_revenue?.toFixed(2)}€</span>
            </div>
          </div>
        )}

        <button onClick={logout} className="absolute bottom-4 left-4 text-gray-400 hover:text-white text-sm">
          Déconnexion
        </button>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        
        {/* ============ SITES VIEW ============ */}
        {view === 'sites' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Sites</h2>
              <button onClick={() => openModal('site')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold">
                + Ajouter un site
              </button>
            </div>

            {/* Filtres */}
            <div className="flex gap-4 mb-6 flex-wrap">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-gray-800 text-white px-4 py-2 rounded outline-none flex-1 min-w-48"
              />
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-gray-800 text-white px-4 py-2 rounded outline-none">
                <option value="all">Tous</option>
                <option value="up">En ligne</option>
                <option value="down">Hors ligne</option>
              </select>
              <select value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); fetchSites(); }} className="bg-gray-800 text-white px-4 py-2 rounded outline-none">
                <option value="">Tous les clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Sites Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredSites.map((site) => (
                <div key={site.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 relative group">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button onClick={() => openHistoryModal(site)} className="text-gray-400 hover:text-white" title="Historique">📊</button>
                    <button onClick={() => openModal('contract', site)} className="text-gray-400 hover:text-white" title="Contrat">📄</button>
                    <button onClick={() => checkSSL(site)} className="text-gray-400 hover:text-white" title="SSL">🔒</button>
                    <button onClick={() => openModal('site', site)} className="text-blue-400 hover:text-blue-300" title="Modifier">✏️</button>
                    <button onClick={() => handleDelete('site', site.id)} className="text-red-400 hover:text-red-300" title="Supprimer">🗑️</button>
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-white truncate pr-8">{site.name}</h3>
                    <span className={`w-3 h-3 rounded-full ${getStatusColor(site)}`}></span>
                  </div>

                  <div className="flex gap-2 mb-3 flex-wrap">
                    {getContractBadge(site.contract)}
                    {site.contract?.end_date && getDaysLeft(site.contract.end_date) <= 90 && getDaysLeft(site.contract.end_date) > 0 && (
                      <span className="text-yellow-400 text-xs">⚠️ Expire dans {getDaysLeft(site.contract.end_date)}j</span>
                    )}
                    {site.ssl_info?.expires_at && getDaysLeft(site.ssl_info.expires_at) <= 30 && getDaysLeft(site.ssl_info.expires_at) > 0 && (
                      <span className="text-orange-400 text-xs">🔒 SSL {getDaysLeft(site.ssl_info.expires_at)}j</span>
                    )}
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-2 truncate">{site.url}</p>
                  
                  {site.clients?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-3">
                      {site.clients.map(c => (
                        <span key={c.id} onClick={() => fetchClientDetail(c.id)} className="text-xs bg-gray-700 px-2 py-1 rounded cursor-pointer hover:bg-gray-600 text-gray-300">
                          {c.name}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <span className={`text-sm ${site.last_check?.is_up ? 'text-green-400' : 'text-red-400'}`}>
                    {getStatusText(site)}
                  </span>
                </div>
              ))}
            </div>

            {filteredSites.length === 0 && (
              <div className="text-center text-gray-500 mt-12">Aucun site trouvé.</div>
            )}
          </>
        )}

        {/* ============ CLIENTS VIEW ============ */}
        {view === 'clients' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Clients</h2>
              <button onClick={() => openModal('client')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold">
                + Ajouter un client
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {clients.map((client) => (
                <div key={client.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 cursor-pointer hover:border-gray-500 transition-colors relative group"
                     onClick={() => fetchClientDetail(client.id)}>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => openModal('client', client)} className="text-blue-400 hover:text-blue-300">✏️</button>
                    <button onClick={() => handleDelete('client', client.id)} className="text-red-400 hover:text-red-300">🗑️</button>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2">{client.name}</h3>
                  
                  {client.email && <p className="text-gray-400 text-sm">📧 {client.email}</p>}
                  {client.phone && <p className="text-gray-400 text-sm">📞 {client.phone}</p>}
                  {client.siret && <p className="text-gray-400 text-sm">🏢 {client.siret}</p>}
                  
                  <div className="flex gap-4 mt-4 pt-4 border-t border-gray-700 text-sm">
                    <span className="text-gray-400">{client.sites_count} site(s)</span>
                    {parseFloat(client.late_amount) > 0 && (
                      <span className="text-red-400">{parseFloat(client.late_amount).toFixed(2)}€ en retard</span>
                    )}
                    {parseFloat(client.pending_amount) > 0 && (
                      <span className="text-yellow-400">{parseFloat(client.pending_amount).toFixed(2)}€ en attente</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {clients.length === 0 && (
              <div className="text-center text-gray-500 mt-12">Aucun client. Cliquez sur "+ Ajouter un client".</div>
            )}
          </>
        )}

        {/* ============ CLIENT DETAIL VIEW ============ */}
        {view === 'client-detail' && selectedClient && (
          <>
            <button onClick={() => setView('clients')} className="text-gray-400 hover:text-white mb-4">
              ← Retour aux clients
            </button>
            
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedClient.name}</h2>
                <div className="text-gray-400 mt-2 space-y-1">
                  {selectedClient.email && <p>📧 {selectedClient.email}</p>}
                  {selectedClient.phone && <p>📞 {selectedClient.phone}</p>}
                  {selectedClient.siret && <p>🏢 SIRET: {selectedClient.siret}</p>}
                  {selectedClient.address && <p>📍 {selectedClient.address}, {selectedClient.postal_code} {selectedClient.city}</p>}
                </div>
              </div>
              <button onClick={() => openModal('client', selectedClient)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                Modifier
              </button>
            </div>

            {/* Stats client */}
            {selectedClient.stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-white">{selectedClient.stats.total_sites}</div>
                  <div className="text-gray-400 text-sm">Sites</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-400">{parseFloat(selectedClient.stats.monthly_recurring || 0).toFixed(2)}€</div>
                  <div className="text-gray-400 text-sm">Récurrent/mois</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-400">{parseFloat(selectedClient.stats.total_paid || 0).toFixed(2)}€</div>
                  <div className="text-gray-400 text-sm">Total payé</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-400">{parseFloat(selectedClient.stats.total_pending || 0).toFixed(2)}€</div>
                  <div className="text-gray-400 text-sm">En attente</div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-400">{parseFloat(selectedClient.stats.total_late || 0).toFixed(2)}€</div>
                  <div className="text-gray-400 text-sm">En retard</div>
                </div>
              </div>
            )}

            {/* Sites du client */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">Sites ({selectedClient.sites?.length || 0})</h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {selectedClient.sites?.map((site) => (
                  <div key={site.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">{site.name}</h4>
                      <span className={`w-3 h-3 rounded-full ${getStatusColor(site)}`}></span>
                    </div>
                    <p className="text-gray-400 text-sm truncate">{site.url}</p>
                    <div className="flex gap-2 mt-2">
                      {getContractBadge(site.contract)}
                      {site.contract?.monthly_amount && (
                        <span className="text-gray-400 text-sm">{site.contract.monthly_amount}€/mois</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Paiements du client */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white">Paiements</h3>
                <button onClick={() => { setPaymentForm({...paymentForm, client_id: selectedClient.id}); openModal('payment'); }} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">
                  + Ajouter
                </button>
              </div>
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left p-3 text-gray-300">Date</th>
                      <th className="text-left p-3 text-gray-300">Site</th>
                      <th className="text-right p-3 text-gray-300">Montant</th>
                      <th className="text-center p-3 text-gray-300">Statut</th>
                      <th className="text-center p-3 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedClient.payments?.map((payment) => (
                      <tr key={payment.id} className="border-t border-gray-700">
                        <td className="p-3 text-gray-300">{new Date(payment.due_date).toLocaleDateString('fr-FR')}</td>
                        <td className="p-3 text-gray-300">{payment.site_name || '-'}</td>
                        <td className="p-3 text-right text-white">{parseFloat(payment.amount).toFixed(2)}€</td>
                        <td className="p-3 text-center">{getPaymentStatusBadge(payment.status)}</td>
                        <td className="p-3 text-center">
                          {payment.status === 'pending' && (
                            <button onClick={() => markPaymentPaid(payment)} className="text-green-400 hover:text-green-300 mr-2">✓</button>
                          )}
                          <button onClick={() => openModal('payment', payment)} className="text-blue-400 hover:text-blue-300 mr-2">✏️</button>
                          <button onClick={() => handleDelete('payment', payment.id)} className="text-red-400 hover:text-red-300">🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!selectedClient.payments || selectedClient.payments.length === 0) && (
                  <p className="text-center text-gray-500 py-4">Aucun paiement</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* ============ PAYMENTS VIEW ============ */}
        {view === 'payments' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Paiements</h2>
              <button onClick={() => openModal('payment')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold">
                + Ajouter un paiement
              </button>
            </div>

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="text-left p-3 text-gray-300">Date</th>
                    <th className="text-left p-3 text-gray-300">Client</th>
                    <th className="text-left p-3 text-gray-300">Site</th>
                    <th className="text-right p-3 text-gray-300">Montant</th>
                    <th className="text-center p-3 text-gray-300">Statut</th>
                    <th className="text-center p-3 text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-t border-gray-700 hover:bg-gray-750">
                      <td className="p-3 text-gray-300">{new Date(payment.due_date).toLocaleDateString('fr-FR')}</td>
                      <td className="p-3 text-white cursor-pointer hover:text-blue-400" onClick={() => fetchClientDetail(payment.client_id)}>{payment.client_name}</td>
                      <td className="p-3 text-gray-300">{payment.site_name || '-'}</td>
                      <td className="p-3 text-right text-white">{parseFloat(payment.amount).toFixed(2)}€</td>
                      <td className="p-3 text-center">{getPaymentStatusBadge(payment.status)}</td>
                      <td className="p-3 text-center">
                        {payment.status === 'pending' && (
                          <button onClick={() => markPaymentPaid(payment)} className="text-green-400 hover:text-green-300 mr-2" title="Marquer payé">✓</button>
                        )}
                        <button onClick={() => openModal('payment', payment)} className="text-blue-400 hover:text-blue-300 mr-2">✏️</button>
                        <button onClick={() => handleDelete('payment', payment.id)} className="text-red-400 hover:text-red-300">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payments.length === 0 && (
                <p className="text-center text-gray-500 py-8">Aucun paiement</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* ============ MODAL ============ */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            
            {/* Site Modal */}
            {modalType === 'site' && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">{editingItem ? 'Modifier le site' : 'Ajouter un site'}</h2>
                {formError && <p className="text-red-400 text-sm mb-4">{formError}</p>}
                <form onSubmit={handleSiteSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">Nom *</label>
                    <input type="text" value={siteForm.name} onChange={(e) => setSiteForm({...siteForm, name: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">URL *</label>
                    <input type="url" value={siteForm.url} onChange={(e) => setSiteForm({...siteForm, url: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-1">Clients</label>
                    <select multiple value={siteForm.client_ids} onChange={(e) => setSiteForm({...siteForm, client_ids: Array.from(e.target.selectedOptions, o => parseInt(o.value))})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none h-32">
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <p className="text-gray-500 text-xs mt-1">Ctrl+clic pour sélectionner plusieurs</p>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={closeModal} className="flex-1 bg-gray-600 hover:bg-gray-500 text-white p-3 rounded">Annuler</button>
                    <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded">{editingItem ? 'Modifier' : 'Ajouter'}</button>
                  </div>
                </form>
              </>
            )}

            {/* Client Modal */}
            {modalType === 'client' && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">{editingItem ? 'Modifier le client' : 'Ajouter un client'}</h2>
                {formError && <p className="text-red-400 text-sm mb-4">{formError}</p>}
                <form onSubmit={handleClientSubmit}>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-sm mb-1">Nom *</label>
                      <input type="text" value={clientForm.name} onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">SIRET</label>
                      <input type="text" value={clientForm.siret} onChange={(e) => setClientForm({...clientForm, siret: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Téléphone</label>
                      <input type="tel" value={clientForm.phone} onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-sm mb-1">Email</label>
                      <input type="email" value={clientForm.email} onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-sm mb-1">Adresse</label>
                      <input type="text" value={clientForm.address} onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Code postal</label>
                      <input type="text" value={clientForm.postal_code} onChange={(e) => setClientForm({...clientForm, postal_code: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Ville</label>
                      <input type="text" value={clientForm.city} onChange={(e) => setClientForm({...clientForm, city: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-sm mb-1">Notes</label>
                      <textarea value={clientForm.notes} onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" rows="2" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={closeModal} className="flex-1 bg-gray-600 hover:bg-gray-500 text-white p-3 rounded">Annuler</button>
                    <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded">{editingItem ? 'Modifier' : 'Ajouter'}</button>
                  </div>
                </form>
              </>
            )}

            {/* Contract Modal */}
            {modalType === 'contract' && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">Contrat - {editingItem?.name}</h2>
                {formError && <p className="text-red-400 text-sm mb-4">{formError}</p>}
                <form onSubmit={handleContractSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">Type</label>
                    <select value={contractForm.type} onChange={(e) => setContractForm({...contractForm, type: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none">
                      <option value="locam">LOCAM</option>
                      <option value="perso">Financement perso</option>
                      <option value="annuel">Annuel</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Date début</label>
                      <input type="date" value={contractForm.start_date} onChange={(e) => setContractForm({...contractForm, start_date: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Date fin</label>
                      <input type="date" value={contractForm.end_date} onChange={(e) => setContractForm({...contractForm, end_date: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">Montant mensuel (€)</label>
                    <input type="number" step="0.01" value={contractForm.monthly_amount} onChange={(e) => setContractForm({...contractForm, monthly_amount: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-1">Notes</label>
                    <textarea value={contractForm.notes} onChange={(e) => setContractForm({...contractForm, notes: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none" rows="2" />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={closeModal} className="flex-1 bg-gray-600 hover:bg-gray-500 text-white p-3 rounded">Annuler</button>
                    <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded">Enregistrer</button>
                  </div>
                </form>
              </>
            )}

            {/* Payment Modal */}
            {modalType === 'payment' && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">{editingItem ? 'Modifier le paiement' : 'Ajouter un paiement'}</h2>
                {formError && <p className="text-red-400 text-sm mb-4">{formError}</p>}
                <form onSubmit={handlePaymentSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">Client *</label>
                    <select value={paymentForm.client_id} onChange={(e) => setPaymentForm({...paymentForm, client_id: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none">
                      <option value="">Sélectionner...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Montant (€) *</label>
                      <input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Date échéance *</label>
                      <input type="date" value={paymentForm.due_date} onChange={(e) => setPaymentForm({...paymentForm, due_date: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Statut</label>
                      <select value={paymentForm.status} onChange={(e) => setPaymentForm({...paymentForm, status: e.target.value})}
                        className="w-full p-3 rounded bg-gray-700 text-white outline-none">
                        <option value="pending">En attente</option>
                        <option value="paid">Payé</option>
                        <option value="late">En retard</option>
                        <option value="cancelled">Annulé</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Méthode</label>
                      <input type="text" value={paymentForm.payment_method} onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                        placeholder="Virement, CB..." className="w-full p-3 rounded bg-gray-700 text-white outline-none" />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-1">Notes</label>
                    <textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                      className="w-full p-3 rounded bg-gray-700 text-white outline-none" rows="2" />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={closeModal} className="flex-1 bg-gray-600 hover:bg-gray-500 text-white p-3 rounded">Annuler</button>
                    <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded">{editingItem ? 'Modifier' : 'Ajouter'}</button>
                  </div>
                </form>
              </>
            )}

            {/* History Modal */}
            {modalType === 'history' && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">Historique - {editingItem?.name}</h2>
                {!historyData ? (
                  <p className="text-gray-400">Chargement...</p>
                ) : (
                  <>
                    <div className="bg-gray-700 p-4 rounded mb-4">
                      <div className="text-2xl font-bold text-green-400">{historyData.uptime}%</div>
                      <div className="text-gray-400 text-sm">Uptime (7 jours)</div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {historyData.history.length === 0 ? (
                        <p className="text-gray-500">Pas encore de données</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-gray-400">
                              <th className="text-left p-2">Heure</th>
                              <th className="text-right p-2">Temps</th>
                              <th className="text-right p-2">Up/Down</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historyData.history.slice(-20).map((h, i) => (
                              <tr key={i} className="border-t border-gray-600">
                                <td className="p-2 text-gray-300">{new Date(h.time_bucket).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                <td className="p-2 text-right text-gray-300">{h.avg_response}ms</td>
                                <td className="p-2 text-right"><span className="text-green-400">{h.up_count}</span>/<span className="text-red-400">{h.down_count}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    <button onClick={closeModal} className="w-full mt-4 bg-gray-600 hover:bg-gray-500 text-white p-3 rounded">Fermer</button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;